import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2, Pause, Play, RotateCcw, X } from 'lucide-react';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import { cn } from '../../../../foundation/altzero_base/base/lib/utils';
import { bvReportService } from '../../services/bvReportService';
import {
  batchProgress, isStartingUp, itemBreakdown, lastActivityLabel, progressSignature,
} from '../../lib/batchProgress';
import { fmtDate } from '../../lib/reportFormulas';
import {
  ACTIVE_BATCH_STATUSES, type BatchStatus, type ReportBatchItemView, type ReportBatchSummary,
} from '../../lib/reportBatchTypes';
import { BatchMultiSiteExportButton } from './BatchMultiSiteExportButton';

type ReportBatchesPanelProps = {
  /** Bump to reload now (e.g. a batch was just created). */
  refreshKey: number;
  /** A batch's counts changed — reload send status on the page. */
  onProgress: () => void;
  /** Open the single-send dialog for a failed bill. */
  onSendAgain: (billId: string) => void;
};

const ACTIVE_POLL_MS = 5_000;
const IDLE_POLL_MS = 60_000;

const STATUS_LABEL: Record<BatchStatus, string> = {
  queued: 'Starting', running: 'Sending', paused: 'Paused', cancelled: 'Cancelled',
  done: 'Done', partial: 'Finished with failures', failed: 'Failed',
};

const isActive = (b: ReportBatchSummary) => ACTIVE_BATCH_STATUSES.includes(b.status);

export const ReportBatchesPanel = ({ refreshKey, onProgress, onSendAgain }: ReportBatchesPanelProps) => {
  const [batches, setBatches] = useState<ReportBatchSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showFinished, setShowFinished] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const signatureRef = useRef<string | null>(null);
  const onProgressRef = useRef(onProgress);
  onProgressRef.current = onProgress;
  // The delay the NEXT tick should use, set from what was just fetched rather
  // than from `batches` state — so a status flip never also changes the
  // polling effect's own dependencies (that would tear down and restart the
  // timer at the same moment the in-flight tick's own setTimeout lands,
  // scheduling two timers for one poll cycle).
  const delayRef = useRef(IDLE_POLL_MS);

  const load = useCallback(async () => {
    const res = await bvReportService.listBatches();
    if (!res.success) { setError(res.error ?? 'Could not load report batches'); return; }
    setError(null);
    const list = res.data ?? [];
    setBatches(list);
    delayRef.current = list.some(isActive) ? ACTIVE_POLL_MS : IDLE_POLL_MS;
    const sig = progressSignature(list);
    if (signatureRef.current !== null && sig !== signatureRef.current) onProgressRef.current();
    signatureRef.current = sig;
  }, []);

  useEffect(() => {
    let mounted = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      await load();
      if (mounted) timer = setTimeout(tick, delayRef.current);
    };
    void tick();
    return () => { mounted = false; if (timer) clearTimeout(timer); };
  }, [load, refreshKey]);

  const act = async (id: string, action: 'pause' | 'resume' | 'cancel') => {
    setBusyId(id);
    const res = await bvReportService.batchAction(id, action);
    setBusyId(null);
    if (!res.success) setError(res.error ?? `Could not ${action} the batch`);
    await load();
  };

  const resend = async (id: string) => {
    setBusyId(id);
    setNotice(null);
    const res = await bvReportService.resendFailed(id);
    setBusyId(null);
    if (!res.success || !res.data) {
      setError(res.error ?? 'Could not resend the failed reports');
    } else {
      const { queued, alreadySent } = res.data;
      setNotice(`Resending ${queued} report${queued === 1 ? '' : 's'} in the background`
        + (alreadySent > 0 ? ` · ${alreadySent} already sent since, left out` : ''));
    }
    await load();
  };

  const active = batches.filter(isActive);
  const finished = batches.filter((b) => !isActive(b));
  if (batches.length === 0 && !error) return null;

  return (
    <section data-testid="report-batches" className="rounded-lg border bg-white">
      <div className="px-4 py-2.5 border-b flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Report batches</p>
        {finished.length > 0 && (
          <button className="text-xs text-gray-500 hover:text-gray-800" onClick={() => setShowFinished((v) => !v)}>
            {showFinished ? 'Hide' : 'Show'} recent ({finished.length})
          </button>
        )}
      </div>
      {error && <p className="px-4 py-2 text-xs text-red-700 bg-red-50">{error}</p>}
      {notice && <p data-testid="report-batch-notice" className="px-4 py-2 text-xs text-emerald-800 bg-emerald-50">{notice}</p>}
      <ul className="divide-y">
        {[...active, ...(showFinished ? finished : [])].map((b) => (
          <BatchRow
            key={b.id}
            batch={b}
            busy={busyId === b.id}
            expanded={expandedId === b.id}
            onToggle={() => setExpandedId((id) => (id === b.id ? null : b.id))}
            onAction={(a) => void act(b.id, a)}
            onResendFailed={() => void resend(b.id)}
            onSendAgain={onSendAgain}
            onExportError={setError}
          />
        ))}
      </ul>
    </section>
  );
};

type BatchRowProps = {
  batch: ReportBatchSummary;
  busy: boolean;
  expanded: boolean;
  onToggle: () => void;
  onAction: (action: 'pause' | 'resume' | 'cancel') => void;
  /** Queue this finished batch's failed bills again as a new batch. */
  onResendFailed: () => void;
  onSendAgain: (billId: string) => void;
  /** The batch's MultiSite export could not be built. */
  onExportError: (message: string) => void;
};

const BatchRow = ({ batch: b, busy, expanded, onToggle, onAction, onResendFailed, onSendAgain, onExportError }: BatchRowProps) => {
  // Emails go to clients: the first click asks, the second sends.
  const [confirming, setConfirming] = useState(false);
  const now = Date.now();
  const p = batchProgress(b, now);
  // Re-rendered on every poll, so this stays current while workers should be
  // running. Hidden when paused: no worker heartbeats then, so the age only grows.
  const lastActivity = b.status === 'queued' || b.status === 'running' ? lastActivityLabel(b.heartbeatAt, now) : null;
  return (
    <li data-testid="report-batch" data-status={b.status} className="px-4 py-3">
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={onToggle} aria-label={expanded ? 'Hide bills' : 'Show bills'} className="text-gray-400 hover:text-gray-700">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <div className="min-w-0 flex-1">
          <p className="text-sm text-gray-900">
            {b.total} report{b.total === 1 ? '' : 's'} · {STATUS_LABEL[b.status]}
            {b.status === 'running' && ` · ${b.liveWorkers} worker${b.liveWorkers === 1 ? '' : 's'}`}
            {p.perMinute !== null && ` · ${p.perMinute}/min`}
          </p>
          <p className="text-xs text-gray-500">
            Started by {b.requestedByName} · {fmtDate(b.createdAt)} · {b.sent} sent · {b.failed} failed · {b.skipped} skipped · {p.remaining} remaining
          </p>
          {isStartingUp(b) && (
            <p data-testid="report-batch-starting" className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" /> Starting workers… the first report usually takes about a minute
            </p>
          )}
          {lastActivity && <p data-testid="report-batch-activity" className="text-xs text-gray-400 mt-0.5">{lastActivity}</p>}
          {b.error && b.status !== 'done' && <p className="text-xs text-amber-700 mt-0.5">{b.error}</p>}
        </div>
        <div className="flex gap-1.5">
          <BatchMultiSiteExportButton batch={b} onError={onExportError} />
          {(b.status === 'queued' || b.status === 'running') && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => onAction('pause')} className="gap-1">
              <Pause className="h-3.5 w-3.5" /> Pause
            </Button>
          )}
          {b.status === 'paused' && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => onAction('resume')} className="gap-1">
              <Play className="h-3.5 w-3.5" /> Resume
            </Button>
          )}
          {isActive(b) && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => onAction('cancel')} className="gap-1">
              <X className="h-3.5 w-3.5" /> Cancel
            </Button>
          )}
          {b.resendable > 0 && !confirming && (
            <Button
              size="sm" variant="outline" disabled={busy} onClick={() => setConfirming(true)}
              data-testid="report-batch-resend" className="gap-1"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
              Resend {b.resendable} failed
            </Button>
          )}
          {b.resendable > 0 && confirming && (
            <>
              <span className="text-xs text-gray-600 self-center">Email {b.resendable} client report{b.resendable === 1 ? '' : 's'} again?</span>
              <Button
                size="sm" disabled={busy} data-testid="report-batch-resend-confirm"
                onClick={() => { setConfirming(false); onResendFailed(); }}
              >
                Resend
              </Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => setConfirming(false)}>No</Button>
            </>
          )}
        </div>
      </div>
      <div className="mt-2 h-2 rounded-full bg-gray-100 overflow-hidden flex">
        <div className="h-full bg-emerald-500" style={{ width: `${b.total ? (b.sent / b.total) * 100 : 0}%` }} />
        <div className="h-full bg-red-500" style={{ width: `${b.total ? (b.failed / b.total) * 100 : 0}%` }} />
        <div className="h-full bg-gray-300" style={{ width: `${b.total ? (b.skipped / b.total) * 100 : 0}%` }} />
      </div>
      {expanded && (
        <BatchItems
          batchId={b.id}
          version={`${b.status}:${b.sent}:${b.failed}:${b.skipped}:${b.heartbeatAt ?? ''}`}
          onSendAgain={onSendAgain}
        />
      )}
    </li>
  );
};

type BatchItemsProps = {
  batchId: string;
  /** Changes whenever the batch's outcome counts or heartbeat move — re-fetches
   * so the bill being sent, and a bill that just failed (or got retried and
   * sent), don't sit stale in the list. */
  version: string;
  onSendAgain: (billId: string) => void;
};

const BatchItems = ({ batchId, version, onSendAgain }: BatchItemsProps) => {
  const [items, setItems] = useState<ReportBatchItemView[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    void bvReportService.getBatch(batchId).then((res) => {
      if (!mounted) return;
      if (res.success && res.data) setItems(res.data.items);
      else setError(res.error ?? 'Could not load the bills in this batch');
    });
    return () => { mounted = false; };
  }, [batchId, version]);

  if (error) return <p className="mt-2 text-xs text-red-700">{error}</p>;
  if (!items) return <p className="mt-2 text-xs text-gray-500 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Loading…</p>;
  const { running, pending, notSent } = itemBreakdown(items);

  return (
    <div className="mt-2 space-y-1.5">
      {running.length > 0 && (
        <ul className="space-y-0.5">
          {running.map((i) => (
            <li key={i.id} data-testid="report-batch-item" data-status={i.status} className="text-xs text-blue-700 flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" /> Sending now · {i.clientName ?? '—'} · <span className="font-mono">{i.nmi ?? '—'}</span>
            </li>
          ))}
        </ul>
      )}
      {pending > 0 && <p data-testid="report-batch-waiting" className="text-xs text-gray-500">{pending} waiting</p>}
      {notSent.length > 0 && <NotSentTable items={notSent} onSendAgain={onSendAgain} />}
      {notSent.length === 0 && running.length === 0 && pending === 0 && (
        <p className="text-xs text-gray-500">No failed or skipped bills.</p>
      )}
    </div>
  );
};

type NotSentTableProps = {
  items: ReportBatchItemView[];
  onSendAgain: (billId: string) => void;
};

const NotSentTable = ({ items, onSendAgain }: NotSentTableProps) => (
  <table className="w-full text-xs">
    <tbody className="divide-y divide-gray-100">
      {items.map((i) => (
        <tr key={i.id} data-testid="report-batch-item" data-status={i.status}>
          <td className="py-1.5 pr-3">
            <p className="font-medium text-gray-900">{i.clientName ?? '—'}</p>
            <p className="font-mono text-gray-500">{i.nmi ?? '—'}</p>
          </td>
          <td className="py-1.5 pr-3 text-gray-600 whitespace-nowrap">
            {i.billFrom && i.billTo ? `${fmtDate(i.billFrom)} – ${fmtDate(i.billTo)}` : '—'}
          </td>
          <td className={cn('py-1.5 pr-3', i.status === 'failed' ? 'text-red-600' : 'text-gray-500')}>{i.error ?? '—'}</td>
          <td className="py-1.5 text-right">
            {(i.status === 'failed' || i.status === 'skipped') && (
              <Button size="sm" variant="ghost" onClick={() => onSendAgain(i.billId)} className="gap-1 h-7">
                <RotateCcw className="h-3 w-3" /> Send again
              </Button>
            )}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);
